import moment from "moment";
import { LoadState } from "../../constants/enums";
import { getUniqueId } from "../../helpers";
import {
  STANDARD_APP_DATE_FORMAT,
  STANDARD_APP_TIME_FORMAT,
} from "../../constants/constants";
import { IFileUpload } from "../../features/FileUpload/components/FileUpload.type";
import { ICustomerContact } from "../customer/customer.types";

export interface IPolicyCommonState {
  contacts: {
    list: ICustomerContact[];
    loading: LoadState;
  };
  telematics: {
    list: IPolicyTelematics[];
    loading: LoadState;
    totalRecrods: number;
  };
  abstracts: {
    list: IPolicyDriverAbstracts[];
    loading: LoadState;
  };
  opcf48: {
    loading: LoadState;
    data: IAutomobileOPCF48;
    error: string | null;
  };
  opcf49: {
    loading: LoadState;
    data: IAutomobileOPCF49;
    error: string | null;
  };
  sef16: {
    loading: LoadState;
    data: IAutomobileSEF16;
    error: string | null;
  };
  sef17: {
    loading: LoadState;
    data: IAutomobileSEF17;
    error: string | null;
  };
  sef19: {
    loading: LoadState;
    data: IAutomobileSEF19;
    error: string | null;
  };
  sef13d: {
    loading: LoadState;
    data: IAutomobileSEF13D;
    error: string | null;
  };
  sef13h: {
    loading: LoadState;
    data: IAutomobileSEF13H;
    error: string | null;
  };
  sef40: {
    loading: LoadState;
    data: IAutomobileSEF40;
    error: string | null;
  };
  opcf47r: {
    loading: LoadState;
    data: IAutomobileOPCF47R;
    error: string | null;
  };
  automobile_insurance: {
    loading: LoadState;
    data: IAutomobileInsurance;
    error: string | null;
  };
  cancellation_reinstatement: {
    loading: LoadState;
    data: IAutomobileCancellationReinstatement;
    error: string | null;
  };
  intact_authorization: {
    loading: LoadState;
    data: IIntactAuthorization;
    error: string | null;
  };
  premiumFinance: {
    data: IPremiumFinance[];
    loading: LoadState;
    totalRecords: number;
    premiumFA: IPremiumFinance;
    premiumFALoading: LoadState;
    error: string | null;
  };
  policyUpdates: {
    list: IPolicyUpdates[];
    loading: LoadState;
  };
  liabilitySlips: {
    data: IPolicyLiabilitySlips;
    dataLoading: LoadState;
    error: string | null;
  };
}

export interface IPolicyLiabilitySlips {
  customer_id: number;
  customer_policy_id: number;
  incremental_number: number | null;
  insured: string | null;
  address: string | null;
  policy_number: string | null;
  policy_status: string | null;
  insurer_name: string | null;
  broker_name: string | null;
  effective_date: string | null;
  expiry_date: string | null;
  // effective_time_of_change: string | null;
  time_of_binding: string | null;
  list_of_vehicle_option_one: string | null;
  list_of_vehicle_option_two: string | null;
  list_of_vehicles: any[];
  quantity_of_slips: number | null;
  producer_one_id: string | null;
  producer_one_code: string | null;
  status: string;
}

export interface IPolicyUpdates {
  policy_update_id: string | null;
  customer_id: number | null;
  customer_policy_id: number | null;
  policy_number: string | null;
  customer_name: string | null;
  customer_email: string | null;
  effective_date: string | null;
  expiry_date: string | null;
  province: string | null;
  gross_revenue: string | null;
  limit_of_liability: string | null;
  premium: number | null;
  tax: string | null;
  total_payable: string | null;
  link_for_payment: string | null;
  attachment: string | null;
  status: string;
}

export interface IPolicyDetail {
  key: string;
  insurer: string | null;
  policy_number: string | null;
  effective_date: string | null;
  expiry_date: string | null;
  transaction_details: string | null;
  line_of_business: string | null;
  premium: number | null;
  mga_fee: number | null;
  broker_fee: number | null;
  sub_total: number | null;
  tax_percentage: number | null;
  tax: number | null;
  total: number | null;
  minimum_retained: number | null;
  minimum_retained_percentage: number | null;
  down_payment: number | null;
}

export interface IPremiumFinance {
  customer_policy_id: number;
  incremental_number: number | null;
  customer_id: number | null;
  insured: string | null;
  contact_name: string | null;
  address: string | null;
  unit_or_suite: string | null;
  city: string | null;
  province_or_state: string | null;
  postal_code: string | null;
  country: string | null;
  phone: string;
  email: string;
  producer: string | null;
  transaction: string | null;
  insurer: string | null;
  policy_details: IPolicyDetail[];
  policy_number: string | null;
  line_of_business: string | null;
  premium: number;
  mga_fee: number;
  broker_fee: number;
  tax: number;
  total: number;
  financed_by: string | null;
  down_payment: string | null;
  dp_collection: string | null;
  minimum_retained: string | null;
  continuous: string | null;
  contract_number: string | null;
  expiry_date: string | null;
  pfa_attachment: IFileUpload[] | null;
  status: string;
  branch_code: string | null;
  payment_received: string | null;
  distribution_date: string | null;
  disbursement_amount: string | null;
  producer_one_code: string | null;
}
export interface IIntactAuthorization {
  province: string | null;
  customer_id: number | null;
  customer_policy_id: number | null;
  policy_number: string | null;
  insurer: string | null;
  customer_name: string | null;
  company_name: string | null;
  branch_name: string | null;
  alternative_withdraw_date_monthly_pay_only: string | null;
  name_of_financial_institution: string | null;
  branch_transit_number: string | null;
  account_number: string | null;
  bank_number: string | null;
  withdrawals: string | null;
  producer_one_id: string | null;
  producer_one_code: string | null;
  pad: string | null;
  name_of_account_holder: string | null;
  policy_type: string | null;
  application_number: string | number | null;
  status: string | null;
}

export interface IInterestedParty {
  key: string;
  interest_party: number | null;
  name: string | null;
  address: string | null;
  interest: string | null;
  isTypeOther: boolean;
}

export interface ICoverageSummary {
  key: string;
  label: string;
  deductible: number | null;
  limit: number | null;
  isTypeOther: boolean;
}

export interface IAutomobileInsurance {
  customer_id: number | null;
  customer_policy_id: number | null;
  incremental_number: number | null;
  producer_one_id: string | null;
  producer_one_code: string | null;
  policy_type: string | null;
  branch_logo: string | null;
  branch_name: string | null;
  branch_address_one: string | null;
  branch_address_two: string | null;
  branch_phone_number: string | null;
  branch_fax: string | null;
  branch_website: string | null;
  insurance_company: string | null;
  policy_number: string | null;
  insured_name: string | null;
  address: string | null;
  effective_date: string | null;
  expiry_date: string | null;
  vehicle_number: number | null;
  year: string | null;
  make: string | null;
  model: string | null;
  vin: string | null;
  interested_parties: IInterestedParty[];
  coverage: number | null;
  coverage_summary: ICoverageSummary[];
  remarks: string | null;
  broker: string | null;
  phone_number: string | null;
  email: string | null;
  contact: string | null;
  producer: string | null;
  phone: string | null;
  phone_ext: string | null;
  signature_of_broker: string | null;
  status: string;
}

export interface IAutomobileCancellationReinstatement {
  customer_policy_id: number | null;
  incremental_number: number | null;
  customer_id: number | null;
  task_code: string;
  customer_code: string | null;
  customer_name: string | null;
  policy_number: string | null;
  bill_type: string | null;
  pc_type: string | null;
  line_of_business: string | null;
  policy_type: string | null;
  branch: string | null;
  broker_code: string | null;
  insurer: string | null;
  producer: string | null;
  producer_one_id: string | null;
  csr: string | null;
  date_of_cancellation: string | null;
  time_of_cancellation: string | null;
  requested_by_insured: string | null;
  rewritten: string | null;
  other: string | null;
  effective_date: string | null;
  expiration_date: string | null;
  insurance_company: string | null;
  flat: string | null;
  short_rate: string | null;
  prorata: string | null;
  premium_paid_to_broker: string | null;
  remarks: string | null;
  insured: string | null;
  mortgagee: string | null;
  lienholder: string | null;
  finance_company: string | null;
  loss_payee: string | null;
  lessor: string | null;
  releases_are_not_attached: string | null;
  file_upload: string | null;
  reinstatement_date: string | null;
  reinstatement_time: string | null;
  reinstatement_full: string | null;
  reinstatement_certified_cheque: string | null;
  reinstatement_partial: string | null;
  reinstatement_balance_owning: string | null;
  reinstatement_reason: string | null;
  signature_date: string | null;
  broker_signature_date: string | null;
  type: string | null;
  remarketed: string | null;
  status: string;
}
export interface IAutomobileOPCF48 {
  customer_policy_id: number | null;
  incremental_number: number | null;
  customer_id: number | null;
  issued_to: string | null;
  policy_number: string | null;
  producer_one_id: string | null;
  producer_one_code: string | null;
  effective_date_of_change: string | null;
  policy_status: string | null;
  insuring_agreement_amount: number | null;
  policy_premium: number | null;
  application_number: string | number | null;
  status: string;
}

export interface IOPCF47RCoverage {
  key: string;
  coverage_included_or_declined: number | null;
  coverage_remarks: string | null;
  benefit: string | null;
  description: string | null;
  example: string | null;
  // isTypeOther: boolean;
}

export interface IAutomobileOPCF47R {
  customer_policy_id: number | null;
  incremental_number: number | null;
  customer_id: number | null;
  lead_id?: string | null;
  issued_to: string | null;
  effective_date_of_change: string | null;
  policy_number: string | null;
  policy_type: string | null;
  producer_one_id: string | null;
  producer_one_code: string | null;
  coverage: IOPCF47RCoverage[];
  application_number: string | number | null;
  status: string;
}

export interface IPolicyVehicle {
  key: string;
  year: string | null;
  trade_name: string | null;
  serial_number_or_vin: string | null;
}

export interface IAutomobileOPCF49 {
  customer_policy_id: number | null;
  incremental_number: number | null;
  customer_id: number | null;
  producer_one_id: string | null;
  producer_one_code: string | null;
  issued_to: string | null;
  policy_type: string | null;
  policy_number: string | null;
  effective_date_of_change: string | null;
  vehicle: IPolicyVehicle[];
  application_number: string | number | null;
  status: string;
}

export interface IAutomobileSEF16 {
  customer_id: number | null;
  customer_policy_id: number | null;
  incremental_number: number | null;
  policy_type: string | null;
  period_fewer_than_consecutive_days: string | null;
  described_under_item_number: string | null;
  date: string | null;
  policy_status: string | null;
  reinstatement_date: string | null;
  producer_one_id: string | null;
  producer_one_code: string | null;
  application_number: string | number | null;
  status: string;
}

export interface IAutomobileSEF17 {
  customer_policy_id: number | null;
  customer_id: number | null;
  incremental_number: number | null;
  policy_type: string | null;
  described_under_item_number: string | null;
  date: string | null;
  policy_status: string | null;
  producer_one_id: string | null;
  producer_one_code: string | null;
  application_number: string | number | null;
  status: string;
}

export interface IAutomobileSEF19 {
  customer_policy_id: number | null;
  incremental_number: number | null;
  customer_id: number | null;
  policy_type: string | null;
  excess_of_the_amount_of: string | null;
  described_under_item_number: string | null;
  date: string | null;
  policy_status: string | null;
  producer_one_id: string | null;
  producer_one_code: string | null;
  application_number: string | number | null;
  status: string;
}

export interface IAutomobileSEF13D {
  customer_policy_id: number | null;
  incremental_number: number | null;
  customer_id: number | null;
  policy_type: string | null;
  described_under_item_number: string | null;
  date: string | null;
  policy_status: string | null;
  producer_one_id: string | null;
  producer_one_code: string | null;
  application_number: string | number | null;
  status: string;
}

export interface IAutomobileSEF13H {
  customer_policy_id: number | null;
  incremental_number: number | null;
  customer_id: number | null;
  policy_type: string | null;
  described_under_item_number: string | null;
  date: string | null;
  policy_status: string | null;
  producer_one_id: string | null;
  producer_one_code: string | null;
  application_number: string | number | null;
  status: string;
}

export interface IAutomobileSEF40 {
  customer_policy_id: number | null;
  incremental_number: number | null;
  customer_id: number | null;
  policy_type: string | null;
  described_under_item_number: string | null;
  date: string | null;
  policy_status: string | null;
  producer_one_id: string | null;
  producer_one_code: string | null;
  application_number: string | number | null;
  status: string;
}
export interface IPolicyDriverAbstracts {
  license_number: string;
  name: string;
  address: string;
  expiry_date: string;
  birthdate: string;
  height: string;
  reference: string;
  comment: string;
  requested_by: string;
  request_on: string;
  user_id: string;
  reply_date: string;
  print_date: string;
  reprint_date: string;
  customer_policy_id: number | null;
  incremental_number: number | null;
  province: string | null;
  licence: string | null;
  request_date: string | null;
  release_date: string | null;
  gender: string | null;
  demerit_points: number | null;
  class: string | null;
  conditions: string | null;
  current_status: string | null;
  issue_date: string | null;
  requested_on: string | null;
  convictions_details: {
    convictions: string;
    date: string;
  }[];
  pt: string | null;
  status: string;
  receipt_number: string | null;
}

export interface IPolicyTelematics {
  customer_policy_id: number | null;
  incremental_number: number | null;
  customer_id: number | null;
  customer_name: string | null;
  insured_name: string | null;
  insurer: string | null;
  policy_number: string | null;
  app_name: string | null;
  status: string;
}

export const policyDetailInitialState: IPolicyDetail = {
    key: getUniqueId(),
    insurer: null,
    policy_number: null,
    effective_date: null,
    expiry_date: null,
    transaction_details: null,
    line_of_business: null,
    premium: null,
    mga_fee: null,
    broker_fee: null,
    sub_total: null,
    tax_percentage: null,
    tax: null,
    total: null,
    minimum_retained: null,
    minimum_retained_percentage: null,
    down_payment: null,
  };

export const opcf47rCoverageInitialState: IOPCF47RCoverage[] = [
  {
    key: getUniqueId(),
    coverage_included_or_declined: null,
    coverage_remarks: null,
    benefit: `Supplementary Medical,
Rehabilitation, and
Attendant Care`,
    description: `This benefit may increase the available limits for
medical, rehabilitation and attendant care
expenses, beyond the standard limits.`,
    example: `If the named insured is seeking medical benefits
beyond the standard limits, these expenses may
be covered.`,
    // isTypeOther: false,
  },
  {
    key: getUniqueId(),
    coverage_included_or_declined: null,
    coverage_remarks: null,
    benefit: `Income Replacement`,
    description: `This benefit may compensate you and other covered
persons for lost income.`,
    example: `If you are employed and sustain injuries in a car
accident that prevents you from working, you may
receive payments for income you are no longer
able to earn.`,
    // isTypeOther: false,
  },
  {
    key: getUniqueId(),
    coverage_included_or_declined: null,
    coverage_remarks: null,
    benefit: `Non-Earner`,
    description: `This benefit may provide compensation if you and
other covered persons are completely unable to
carry on a normal life.`,
    example: `If you are a student or unemployed and sustain
injuries that prevent you from leading a normal
life, you may receive weekly non-earner
compensation.`,
    // isTypeOther: false,
  },
  {
    key: getUniqueId(),
    coverage_included_or_declined: null,
    coverage_remarks: null,
    benefit: `Caregiver – Catastrophic
Impairment only`,
    description: `This benefit may provide compensation for some
expenses incurred if a covered person has been
catastrophically injured and cannot continue as
the main caregiver for a member of the household
who is in need of care.`,
    example: `If you care for a dependant and can no longer do
so due to catastrophic injuries, you may receive
compensation for hired caregiver support.`,
    // isTypeOther: false,
  },
  {
    key: getUniqueId(),
    coverage_included_or_declined: null,
    coverage_remarks: null,
    benefit: `Caregiver – Impairment`,
    description: `This benefit may provide compensation for some
expenses incurred if a covered person has been
injured and cannot continue as the main caregiver
for a member of the household who is in need of
care.`,
    example: `If you care for a dependant and can no longer do
so due to injuries, you may receive compensation
for hired caregiver support.`,
    // isTypeOther: false,
  },
  {
    key: getUniqueId(),
    coverage_included_or_declined: null,
    coverage_remarks: null,
    benefit: `Lost Educational
Expenses`,
    description: `This benefit may provide compensation if you and
other covered persons are unable to continue a
program of elementary, secondary, post-secondary
or continuing education as a result of an
accident.`,
    example: `If you are enrolled in a program and unable to
continue due to accident injuries, lost tuition,
book costs, or other related expenses may be
covered.`,
    // isTypeOther: false,
  },

  // ---------- NEW ADDITIONS ----------
  {
    key: getUniqueId(),
    coverage_included_or_declined: null,
    coverage_remarks: null,
    benefit: `Expenses of Visitors`,
    description: `This benefit may provide compensation for
reasonable and necessary expenses incurred by
visitors if you and other covered persons sustain
an impairment as a result of an accident.`,
    example: `If your parents or children travel to visit you
during recovery, their transportation and lodging
costs may be covered.`,
    // isTypeOther: false,
  },
  {
    key: getUniqueId(),
    coverage_included_or_declined: null,
    coverage_remarks: null,
    benefit: `Housekeeping & Home
Maintenance –
Catastrophic Impairment
only`,
    description: `This benefit may provide compensation if you and
other covered persons suffer a substantial
inability to perform the housekeeping and home
maintenance services that you normally
performed before the accident, as a result of a
catastrophic injury.`,
    example: `If you are catastrophically injured and unable to
maintain your home, expenses for a cleaning or
maintenance service may be covered.`,
    // isTypeOther: false,
  },
  {
    key: getUniqueId(),
    coverage_included_or_declined: null,
    coverage_remarks: null,
    benefit: `Housekeeping & Home
Maintenance –
Impairment`,
    description: `This benefit may provide compensation if you and
other covered persons suffer a substantial
inability to perform the housekeeping and home
maintenance services that you normally
performed before the accident.`,
    example: `If you are injured and unable to maintain your
home, expenses for a cleaning or maintenance
service may be covered.`,
    // isTypeOther: false,
  },
  {
    key: getUniqueId(),
    coverage_included_or_declined: null,
    coverage_remarks: null,
    benefit: `Damage to Personal
Items (clothing,
glasses, hearing
aids, etc.)`,
    description: `This benefit may provide compensation if you and
other covered persons sustain damage to
clothing, prescription eyewear, dentures, hearing
aids, prostheses and other medical or dental
devices that were lost or damaged as a result of
the accident.`,
    example: `If your eyeglasses or clothing are damaged in a
motor vehicle accident, these expenses may be
covered.`,
    // isTypeOther: false,
  },
  {
    key: getUniqueId(),
    coverage_included_or_declined: null,
    coverage_remarks: null,
    benefit: `Death`,
    description: `This benefit may pay money to some members of
the family if you or other covered persons has
been killed.`,
    example: `If the named insured dies due to a motor vehicle
accident, surviving family members may receive a
death benefit.`,
    // isTypeOther: false,
  },
  {
    key: getUniqueId(),
    coverage_included_or_declined: null,
    coverage_remarks: null,
    benefit: `Funeral`,
    description: `This benefit may pay for some funeral expenses.`,
    example: `Funeral expenses for an insured person who dies
as a result of a collision may be paid.`,
    // isTypeOther: false,
  },
  {
    key: getUniqueId(),
    coverage_included_or_declined: null,
    coverage_remarks: null,
    benefit: `Dependant Care`,
    description: `This benefit may cover reasonable and necessary
expenses incurred in caring for dependants.`,
    example: `If the named insured is not receiving a caregiver
benefit, was employed at the time of the
accident, and sustains an impairment, daycare
services for a dependant may be paid.`,
    // isTypeOther: false,
  },
  {
    key: getUniqueId(),
    coverage_included_or_declined: null,
    coverage_remarks: null,
    benefit: `Indexation`,
    description: `This benefit provides that certain weekly benefit
payments and monetary limits will be adjusted on
an annual basis to reflect changes in the cost of
living.`,
    example: `Each year, the income replacement benefit
payable is adjusted to reflect cost of living
changes.`,
    // isTypeOther: false,
  },
  {
    key: getUniqueId(),
    coverage_included_or_declined: null,
    coverage_remarks: null,
    benefit: null,
    description: null,
    example: null,
    // isTypeOther: true,
  },
  {
    key: getUniqueId(),
    coverage_included_or_declined: null,
    coverage_remarks: null,
    benefit: null,
    description: null,
    example: null,
    // isTypeOther: true,
  },
  {
    key: getUniqueId(),
    coverage_included_or_declined: null,
    coverage_remarks: null,
    benefit: null,
    description: null,
    example: null,
    // isTypeOther: true,
  },
];


export const initialPolicyCommonState: IPolicyCommonState = {
  contacts: {
    list: [],
    loading: LoadState.NotLoaded,
  },
  telematics: {
    list: [],
    loading: LoadState.NotLoaded,
    totalRecrods: 0,
  },
  abstracts: {
    list: [],
    loading: LoadState.NotLoaded,
  },
  opcf48: {
    loading: LoadState.NotLoaded,
    data: {
      customer_policy_id: null,
      incremental_number: null,
      customer_id: null,
      issued_to: null,
      producer_one_id: null,
      producer_one_code: null,
      policy_number: null,
      effective_date_of_change: null,
      policy_status: null,
      policy_premium: null,
      status: "ACTIVE",
      insuring_agreement_amount: null,
      application_number: null,
    },
    error: null,
  },
  opcf47r: {
    loading: LoadState.NotLoaded,
    data: {
      customer_policy_id: null,
      incremental_number: null,
      customer_id: null,
      issued_to: null,
      policy_type: null,
      effective_date_of_change: null,
      policy_number: null,
      producer_one_id: null,
      producer_one_code: null,
      coverage: opcf47rCoverageInitialState,
      status: "ACTIVE",
      application_number: null,
    },
    error: null,
  },
  opcf49: {
    loading: LoadState.NotLoaded,
    data: {
      customer_policy_id: null,
      incremental_number: null,
      customer_id: null,
      producer_one_id: null,
      producer_one_code: null,
      policy_type: null,
      issued_to: null,
      policy_number: null,
      effective_date_of_change: null,
      vehicle: [
        {
          key: getUniqueId(),
          year: null,
          trade_name: null,
          serial_number_or_vin: null,
        },
        {
          key: getUniqueId(),
          year: null,
          trade_name: null,
          serial_number_or_vin: null,
        },
        {
          key: getUniqueId(),
          year: null,
          trade_name: null,
          serial_number_or_vin: null,
        },
      ],
      status: "ACTIVE",
      application_number: null,
    },
    error: null,
  },
  sef16: {
    loading: LoadState.NotLoaded,
    data: {
      customer_policy_id: null,
      incremental_number: null,
      policy_type: null,
      customer_id: null,
      period_fewer_than_consecutive_days: null,
      described_under_item_number: null,
      date: null,
      policy_status: null,
      producer_one_id: null,
      producer_one_code: null,
      application_number: null,
      status: "ACTIVE",
      reinstatement_date: null,
    },
    error: null,
  },
  sef17: {
    loading: LoadState.NotLoaded,
    data: {
      customer_policy_id: null,
      incremental_number: null,
      policy_type: null,
      customer_id: null,
      described_under_item_number: null,
      date: null,
      policy_status: null,
      producer_one_id: null,
      producer_one_code: null,
      application_number: null,
      status: "ACTIVE",
    },
    error: null,
  },
  sef19: {
    loading: LoadState.NotLoaded,
    data: {
      customer_policy_id: null,
      incremental_number: null,
      policy_type: null,
      customer_id: null,
      excess_of_the_amount_of: null,
      described_under_item_number: null,
      date: null,
      policy_status: null,
      status: "ACTIVE",
      producer_one_id: null,
      producer_one_code: null,
      application_number: null,
    },
    error: null,
  },
  sef13d: {
    loading: LoadState.NotLoaded,
    data: {
      customer_policy_id: null,
      incremental_number: null,
      policy_type: null,
      customer_id: null,
      described_under_item_number: null,
      date: null,
      policy_status: null,
      status: "ACTIVE",
      producer_one_id: null,
      producer_one_code: null,
      application_number: null,
    },
    error: null,
  },
  sef13h: {
    loading: LoadState.NotLoaded,
    data: {
      customer_policy_id: null,
      incremental_number: null,
      policy_type: null,
      customer_id: null,
      described_under_item_number: null,
      date: null,
      policy_status: null,
      status: "ACTIVE",
      producer_one_id: null,
      producer_one_code: null,
      application_number: null,
    },
    error: null,
  },
  sef40: {
    loading: LoadState.NotLoaded,
    data: {
      customer_policy_id: null,
      incremental_number: null,
      policy_type: null,
      customer_id: null,
      described_under_item_number: null,
      date: null,
      policy_status: null,
      status: "ACTIVE",
      producer_one_id: null,
      producer_one_code: null,
      application_number: null,
    },
    error: null,
  },
  automobile_insurance: {
    loading: LoadState.NotLoaded,
    data: {
      customer_id: null,
      producer_one_id: null,
      producer_one_code: null,
      customer_policy_id: null,
      incremental_number: null,
      policy_type: null,
      branch_logo: null,
      branch_name: null,
      branch_address_one: null,
      branch_address_two: null,
      branch_phone_number: null,
      branch_fax: null,
      branch_website: null,
      insurance_company: null,
      policy_number: null,
      insured_name: null,
      address: null,
      effective_date: moment().format(STANDARD_APP_DATE_FORMAT),
      expiry_date: null,
      year: null,
      make: null,
      model: null,
      vin: null,
      interested_parties: [
        {
          key: getUniqueId(),
          interest_party: null,
          name: null,
          address: null,
          interest: null,
          isTypeOther: false,
        },
        {
          key: getUniqueId(),
          interest_party: null,
          name: null,
          address: null,
          interest: null,
          isTypeOther: false,
        },
      ],
      coverage_summary: [
        {
          key: getUniqueId(),
          label: "Liability",
          deductible: null,
          limit: null,
          isTypeOther: false,
        },
        {
          key: getUniqueId(),
          label: "Specified Perils (excluding Collision or Upset)",
          deductible: null,
          limit: null,
          isTypeOther: false,
        },
        {
          key: getUniqueId(),
          label: "Collision or Upset",
          deductible: null,
          limit: null,
          isTypeOther: false,
        },
        {
          key: getUniqueId(),
          label: "Comprehensive (excluding Collision or Upset)",
          deductible: null,
          limit: null,
          isTypeOther: false,
        },
        {
          key: getUniqueId(),
          label: "All Perils",
          deductible: null,
          limit: null,
          isTypeOther: false,
        },
        {
          key: getUniqueId(),
          label:
            "Policy Change Forms (Name & No.)Family Protection Coverage - OPCF 44R",
          deductible: null,
          limit: null,
          isTypeOther: false,
        },
        {
          key: getUniqueId(),
          label: "",
          deductible: null,
          limit: null,
          isTypeOther: true,
        },
        {
          key: getUniqueId(),
          label: "",
          deductible: null,
          limit: null,
          isTypeOther: true,
        },
        {
          key: getUniqueId(),
          label: "",
          deductible: null,
          limit: null,
          isTypeOther: true,
        },
        {
          key: getUniqueId(),
          label: "",
          deductible: null,
          limit: null,
          isTypeOther: true,
        },
      ],
      remarks: null,
      broker: null,
      phone_number: null,
      email: null,
      contact: null,
      producer: null,
      phone: null,
      phone_ext: null,
      signature_of_broker: null,
      status: "ACTIVE",
      vehicle_number: null,
      coverage: null,
    },
    error: null,
  },
  cancellation_reinstatement: {
    loading: LoadState.NotLoaded,
    data: {
      customer_policy_id: null,
      incremental_number: null,
      customer_id: null,
      task_code: "OPCFD",
      customer_code: null,
      customer_name: null,
      policy_number: null,
      bill_type: null,
      pc_type: null,
      line_of_business: null,
      policy_type: null,
      branch: null,
      broker_code: null,
      insurer: null,
      producer: null,
      producer_one_id: null,
      csr: null,
      date_of_cancellation: null,
      time_of_cancellation: "00:01:00",
      requested_by_insured: null,
      rewritten: null,
      other: null,
      effective_date: null,
      expiration_date: null,
      insurance_company: null,
      flat: null,
      short_rate: null,
      prorata: null,
      premium_paid_to_broker: null,
      remarks: null,
      insured: null,
      mortgagee: null,
      lienholder: null,
      finance_company: null,
      loss_payee: null,
      lessor: null,
      releases_are_not_attached: null,
      file_upload: null,
      reinstatement_date: null,
      reinstatement_time: null,
      reinstatement_full: null,
      reinstatement_certified_cheque: null,
      reinstatement_partial: null,
      reinstatement_balance_owning: null,
      reinstatement_reason: null,
      signature_date: moment().format("YYYY-MM-DD"),
      broker_signature_date: moment().format("YYYY-MM-DD"),
      type: "cancellation",
      remarketed: null,
      status: "ACTIVE",
    },
    error: null,
  },
  intact_authorization: {
    loading: LoadState.NotLoaded,
    data: {
      province: "ON",
      customer_id: null,
      customer_policy_id: null,
      policy_number: null,
      insurer: null,
      account_number: null,
      alternative_withdraw_date_monthly_pay_only: null,
      bank_number: null,
      branch_transit_number: null,
      company_name: null,
      customer_name: null,
      name_of_account_holder: null,
      name_of_financial_institution: null,
      pad: "Personal PAD",
      status: "ACTIVE",
      withdrawals: "Monthly Withdrawals",
      policy_type: null,
      branch_name: null,
      producer_one_id: null,
      producer_one_code: null,
      application_number: null,
    },
    error: null,
  },
  premiumFinance: {
    data: [],
    loading: LoadState.NotLoaded,
    totalRecords: 0,
    premiumFA: {
      customer_policy_id: 0,
      incremental_number: null,
      customer_id: null,
      insured: null,
      contact_name: null,
      address: null,
      unit_or_suite: null,
      city: null,
      province_or_state: null,
      postal_code: null,
      country: null,
      phone: "",
      email: "",
      producer: null,
      branch_code: null,
      transaction: null,
      insurer: null,
      policy_details: [policyDetailInitialState],
      policy_number: null,
      line_of_business: null,
      premium: 0,
      mga_fee: 0,
      broker_fee: 0,
      tax: 0,
      total: 0,
      financed_by: null,
      down_payment: null,
      dp_collection: null,
      minimum_retained: null,
      continuous: null,
      contract_number: null,
      expiry_date: null,
      pfa_attachment: null,
      status: "NEW",
      disbursement_amount: null,
      distribution_date: null,
      payment_received: null, 
      producer_one_code: null,
    },
    premiumFALoading: LoadState.NotLoaded,
    error: null,
  },
  policyUpdates: {
    list: [],
    loading: LoadState.NotLoaded,
  },
  liabilitySlips: {
    data: {
      customer_id: 0,
      producer_one_id: null,
      producer_one_code: null,
      customer_policy_id: 0,
      incremental_number: null,
      insured: null,
      address: null,
      policy_number: null,
      policy_status: null,
      insurer_name: null,
      broker_name: null,
      effective_date: moment().format(STANDARD_APP_DATE_FORMAT),
      expiry_date: moment().format(STANDARD_APP_TIME_FORMAT),
      list_of_vehicle_option_one: null,
      list_of_vehicle_option_two: "Select Vehicle",
      list_of_vehicles: [],
      status: "",
      quantity_of_slips: null,
      time_of_binding: null,
    },
    dataLoading: LoadState.NotLoaded,
    error: null,
  },
};

export const initialPolicyTelematics: IPolicyTelematics = {
  customer_id: null,
  app_name: null,
  customer_policy_id: null,
  incremental_number: null,
  customer_name: null,
  insured_name: null,
  insurer: null,
  policy_number: null,
  status: "ACTIVE",
};
